import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
export function QuoteForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    description: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        description: ''
      });
      // Reset success message after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return <section id="quote" className="py-20 lg:py-28 bg-brand-navy relative overflow-hidden" data-id="element-215">
      {/* Background Texture */}
      <div className="absolute inset-0 z-0 opacity-10 mix-blend-overlay" data-id="element-216">
        <img src="https://images.unsplash.com/photo-1585704032915-c3400ca199e7?q=80&w=2070&auto=format&fit=crop" alt="Plumbing pipes texture" className="w-full h-full object-cover" data-id="element-217" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-218">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-219">
          {/* Left Column: Pitch */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-white" data-id="element-220">
            <span className="inline-block py-1 px-3 rounded bg-brand-red/20 border border-brand-red/30 text-brand-red font-bold tracking-wider uppercase text-sm mb-6 backdrop-blur-sm" data-id="element-221">
              Get A Free Quote
            </span>
            <h2 className="text-4xl md:text-5xl font-display font-bold uppercase mb-6 leading-tight" data-id="element-222">
              Need a Plumber? Get a Free Quote in 60 Seconds.
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed" data-id="element-223">
              Don't let plumbing issues disrupt your day. Fill out the form, and
              our dispatch team will contact you within 1 business hour with a
              no-obligation estimate.
            </p>

            <ul className="space-y-4 mb-8" data-id="element-224">
              {['Free, no-obligation estimate', 'Response within 1 hour', 'Upfront, honest pricing', 'Licensed & insured professionals'].map((benefit, i) => <li key={i} className="flex items-center gap-3" data-id="element-225">
                  <CheckCircle2 className="w-6 h-6 text-brand-red shrink-0" data-id="element-226" />
                  <span className="font-medium text-lg" data-id="element-227">{benefit}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Right Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-228">
            <div className="bg-white rounded-md shadow-2xl p-8 relative overflow-hidden" data-id="element-229">
              {isSuccess ? <div className="absolute inset-0 bg-white flex flex-col items-center justify-center p-8 text-center z-20" data-id="element-230">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-231">
                    <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-232" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-brand-navy uppercase mb-2" data-id="element-233">
                    Quote Request Sent!
                  </h3>
                  <p className="text-gray-600" data-id="element-234">
                    Thanks, {formData.name || 'there'}! We will contact you
                    within 1 hour.
                  </p>
                </div> : null}

              <form onSubmit={handleSubmit} className="space-y-5 relative z-10" data-id="element-235">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-236">
                  <div data-id="element-237">
                    <label htmlFor="name" className="sr-only" data-id="element-238">
                      Full Name
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="Full Name *" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all bg-gray-50 focus:bg-white" data-id="element-239" />
                  </div>
                  <div data-id="element-240">
                    <label htmlFor="phone" className="sr-only" data-id="element-241">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} placeholder="Phone Number *" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all bg-gray-50 focus:bg-white" data-id="element-242" />
                  </div>
                </div>

                <div data-id="element-243">
                  <label htmlFor="email" className="sr-only" data-id="element-244">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address (Optional)" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all bg-gray-50 focus:bg-white" data-id="element-245" />
                </div>

                <div data-id="element-246">
                  <label htmlFor="service" className="sr-only" data-id="element-247">
                    Service Needed
                  </label>
                  <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all bg-gray-50 focus:bg-white text-gray-700" data-id="element-248">
                    <option value="" disabled data-id="element-249">
                      Select Service Type...
                    </option>
                    <option value="residential" data-id="element-250">Residential Plumbing</option>
                    <option value="commercial" data-id="element-251">Commercial Plumbing</option>
                    <option value="emergency" data-id="element-252">Emergency Service</option>
                    <option value="water-heater" data-id="element-253">Water Heater</option>
                    <option value="drain" data-id="element-254">Drain & Sewer</option>
                    <option value="other" data-id="element-255">Other</option>
                  </select>
                </div>

                <div data-id="element-256">
                  <label htmlFor="description" className="sr-only" data-id="element-257">
                    Brief Description
                  </label>
                  <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleChange} placeholder="Briefly describe your plumbing issue..." className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all bg-gray-50 focus:bg-white resize-none" data-id="element-258"></textarea>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-brand-red hover:bg-brand-redDark text-white font-bold py-4 rounded-md uppercase tracking-wide transition-colors flex items-center justify-center gap-2 disabled:opacity-70" data-id="element-259">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-260" /> Sending...
                    </> : 'Get My Free Quote →'}
                </button>

                <p className="text-xs text-center text-gray-500 mt-4" data-id="element-261">
                  We respect your privacy. We will never share your info.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}